import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import {
  FileCode2, Plus, Trash2, Search, Check, ShieldAlert, Save, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_STYLE = {
  critical: "border-rose-500/60 text-rose-300 bg-rose-500/10",
  high: "border-orange-500/60 text-orange-300 bg-orange-500/10",
  medium: "border-amber-500/60 text-amber-300 bg-amber-500/10",
  low: "border-cyan-500/60 text-cyan-300 bg-cyan-500/10",
  info: "border-slate-600 text-slate-300 bg-slate-800/60",
};

const SEVERITIES = ["critical", "high", "medium", "low", "info"];

const NucleiTemplates = () => {
  const { active, activeId } = useEngagement();
  const isDemo = !activeId || activeId === "demo-default";

  const [available, setAvailable] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [origSelected, setOrigSelected] = useState([]);
  const [filter, setFilter] = useState("");
  const [sevFilter, setSevFilter] = useState("");
  const [busy, setBusy] = useState(false);

  // create form
  const [showForm, setShowForm] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplSeverity, setTplSeverity] = useState("medium");
  const [tplTags, setTplTags] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [tplYaml, setTplYaml] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      if (isDemo) {
        const r = await api.get("/nuclei/templates");
        setAvailable(r.data);
        setSelectedIds([]);
        setOrigSelected([]);
      } else {
        const r = await api.get(`/engagements/${activeId}/nuclei-templates`);
        setAvailable(r.data.available);
        setSelectedIds(r.data.selected_ids || []);
        setOrigSelected(r.data.selected_ids || []);
      }
    } catch (e) {
      console.error("[templates] load:", e);
    }
  }, [activeId, isDemo]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return available.filter((t) => {
      if (sevFilter && t.severity !== sevFilter) return false;
      if (!q) return true;
      const hay = `${t.name} ${t.description || ""} ${(t.tags || []).join(" ")} ${t.id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [available, filter, sevFilter]);

  const toggle = (id) => {
    if (isDemo) return;
    setSelectedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const dirty = useMemo(() => {
    const a = [...selectedIds].sort().join(",");
    const b = [...origSelected].sort().join(",");
    return a !== b;
  }, [selectedIds, origSelected]);

  const save = async () => {
    if (isDemo) return;
    setBusy(true);
    try {
      await api.put(`/engagements/${activeId}/nuclei-templates`, { selected_ids: selectedIds });
      toast.success(`Saved · ${selectedIds.length} template(s) enabled`);
      setOrigSelected(selectedIds);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const selectAllVisible = () => {
    if (isDemo) return;
    const visIds = filtered.map((t) => t.id);
    setSelectedIds((cur) => Array.from(new Set([...cur, ...visIds])));
  };
  const clearAllVisible = () => {
    if (isDemo) return;
    const visIds = new Set(filtered.map((t) => t.id));
    setSelectedIds((cur) => cur.filter((id) => !visIds.has(id)));
  };

  const createTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name required"); return; }
    setCreating(true);
    try {
      const tags = tplTags.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/nuclei/templates", {
        name: tplName.trim(), severity: tplSeverity, tags,
        description: tplDesc.trim() || null, raw_yaml: tplYaml || null,
      });
      toast.success(`Created template ${data.id}`);
      setShowForm(false);
      setTplName(""); setTplTags(""); setTplDesc(""); setTplYaml("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this custom template?")) return;
    try {
      await api.delete(`/nuclei/templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Live Operations / Templates"
        title="Nuclei Template Management"
        subtitle="Curate which Nuclei detection templates run on this engagement. Built-in CyberSphereX library + custom uploads. Selections apply only to live Nuclei scans (simulated mode runs all)."
      />

      {isDemo && (
        <div className="csx-card border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <div className="text-amber-300 uppercase tracking-widest font-bold">Read-only on Demo Environment</div>
            <div className="text-slate-400 mt-1">Switch to a real engagement to enable/disable templates per scan run.</div>
          </div>
        </div>
      )}

      <div className="csx-card p-4 mb-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="tpl-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name, tag, CVE..."
              className="w-full bg-slate-950 border border-slate-800 pl-9 pr-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70"
            />
          </div>
          <div className="flex gap-1">
            <button
              data-testid="sev-filter-all"
              onClick={() => setSevFilter("")}
              className={["text-[10px] uppercase tracking-widest px-2 py-1 border",
                sevFilter === "" ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : "border-slate-800 text-slate-400",
              ].join(" ")}
            >ALL</button>
            {SEVERITIES.map((s) => (
              <button
                key={s}
                data-testid={`sev-filter-${s}`}
                onClick={() => setSevFilter(s)}
                className={["text-[10px] uppercase tracking-widest px-2 py-1 border",
                  sevFilter === s ? SEVERITY_STYLE[s] : "border-slate-800 text-slate-400 hover:border-slate-700",
                ].join(" ")}
              >{s}</button>
            ))}
          </div>
          {!isDemo && (
            <div className="flex gap-2">
              <button data-testid="tpl-select-visible" onClick={selectAllVisible}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-slate-800 text-slate-300 hover:border-cyan-500/40">
                Select visible
              </button>
              <button data-testid="tpl-clear-visible" onClick={clearAllVisible}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-slate-800 text-slate-300 hover:border-rose-500/40">
                Clear visible
              </button>
            </div>
          )}
          <button
            data-testid="tpl-new-toggle"
            onClick={() => setShowForm((v) => !v)}
            className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fuchsia-500/60 text-fuchsia-300 hover:bg-fuchsia-500/10 flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> {showForm ? "Close" : "New Template"}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="csx-card p-5 mb-4 border-fuchsia-500/30 bg-fuchsia-500/[0.04]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300 mb-3">New Custom Template</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Name</div>
              <input data-testid="tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm" placeholder="My Custom Detection" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Severity</div>
              <select data-testid="tpl-severity" value={tplSeverity} onChange={(e) => setTplSeverity(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Tags (comma-separated)</div>
              <input data-testid="tpl-tags" value={tplTags} onChange={(e) => setTplTags(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono" placeholder="cve, sqli, custom" />
            </div>
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Description</div>
              <input data-testid="tpl-desc" value={tplDesc} onChange={(e) => setTplDesc(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm" />
            </div>
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Raw YAML (optional)</div>
              <textarea data-testid="tpl-yaml" value={tplYaml} onChange={(e) => setTplYaml(e.target.value)} rows={8}
                className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-xs font-mono"
                placeholder="id: my-detection\ninfo:\n  name: ...\n  severity: medium\n..." />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <TButton data-testid="tpl-create-btn" icon={creating ? Loader2 : Plus} onClick={createTemplate} disabled={creating}>
              {creating ? "Creating..." : "Create Template"}
            </TButton>
          </div>
        </div>
      )}

      {/* Save bar */}
      {!isDemo && (
        <div className={[
          "csx-card p-3 mb-4 flex items-center justify-between sticky top-2 z-10 backdrop-blur",
          dirty ? "border-cyan-400/60 bg-cyan-500/5" : "border-slate-800",
        ].join(" ")}>
          <div className="text-xs">
            <span className="text-slate-400">Engagement: </span>
            <span className="text-cyan-300 font-semibold">{active?.name || "—"}</span>
            <span className="text-slate-500 mx-2">·</span>
            <span data-testid="tpl-selected-count" className="text-fuchsia-300 font-mono">{selectedIds.length}</span>
            <span className="text-slate-500"> of {available.length} enabled</span>
            {dirty && <span className="ml-3 text-[10px] uppercase tracking-widest text-amber-300">unsaved</span>}
          </div>
          <TButton data-testid="tpl-save-btn" icon={busy ? Loader2 : Save} onClick={save} disabled={busy || !dirty}>
            {busy ? "Saving..." : "Save Selection"}
          </TButton>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map((t) => {
          const isSelected = selectedIds.includes(t.id);
          return (
            <div
              key={t.id}
              data-testid={`tpl-card-${t.id}`}
              onClick={() => toggle(t.id)}
              className={[
                "csx-card p-4 cursor-pointer transition-all relative group",
                isSelected ? "border-cyan-400 bg-cyan-500/5" : "border-slate-800 hover:border-slate-700",
                isDemo ? "cursor-not-allowed opacity-60" : "",
              ].join(" ")}
            >
              <div className="flex items-start gap-2 mb-2">
                <FileCode2 className={["h-4 w-4 mt-0.5 shrink-0", isSelected ? "text-cyan-300" : "text-slate-500"].join(" ")} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 font-semibold truncate">{t.name}</div>
                  <div className="text-[10px] font-mono text-slate-500 truncate">{t.id}</div>
                </div>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${SEVERITY_STYLE[t.severity] || ""}`}>
                  {t.severity}
                </span>
              </div>
              {t.description && (
                <div className="text-[11px] text-slate-400 mb-2 line-clamp-2">{t.description}</div>
              )}
              <div className="flex flex-wrap gap-1 mb-2">
                {(t.tags || []).slice(0, 6).map((tag) => (
                  <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-slate-900 border border-slate-800 text-slate-400 font-mono">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span>
                  <span className={t.source === "builtin" ? "text-cyan-400" : "text-fuchsia-400"}>
                    {t.source === "builtin" ? "BUILT-IN" : "CUSTOM"}
                  </span>
                  {t.author && <span className="ml-2">by {t.author}</span>}
                </span>
                <div className="flex items-center gap-2">
                  {isSelected && <Check className="h-3.5 w-3.5 text-cyan-300" />}
                  {t.source === "custom" && (
                    <button
                      data-testid={`tpl-delete-${t.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                      className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete custom template"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="csx-card p-8 text-center text-slate-500 text-sm">
          No templates match your filter.
        </div>
      )}
    </div>
  );
};

export default NucleiTemplates;
